import { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Film } from 'lucide-react';
import { TimelineCanvas } from './TimelineCanvas';
import { TimelineControls } from './TimelineControls';
import { TrackLabels } from './TrackLabels';
import { useImportToProject } from '../hooks/useImportToProject';
import { useEditorStore } from '../store/editorStore';

export function TimelinePanel() {
  const labelsRef = useRef<HTMLDivElement>(null);
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { importAsset } = useImportToProject();
  const hasTracks = useEditorStore((s) => s.project.tracks.length > 0);
  const [dragOver, setDragOver] = useState(false);

  // Sync vertical scroll between labels and canvas
  const onCanvasScroll = useCallback(() => {
    if (canvasWrapRef.current && labelsRef.current) {
      labelsRef.current.scrollTop = canvasWrapRef.current.scrollTop;
    }
  }, []);

  const onLabelsScroll = useCallback(() => {
    if (labelsRef.current && canvasWrapRef.current) {
      canvasWrapRef.current.scrollTop = labelsRef.current.scrollTop;
    }
  }, []);

  return (
    <div
      className={`flex flex-col h-full bg-[#1A1A1E] overflow-hidden transition-all ${dragOver ? 'ring-2 ring-[var(--user-page-accent)] ring-inset' : ''}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('application/x-vidnight-asset')) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          setDragOver(true);
        }
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const assetId = e.dataTransfer.getData('application/x-vidnight-asset');
        if (assetId) importAsset(assetId);
      }}
    >
      <TimelineControls />
      {hasTracks ? (
        <div className="flex-1 flex min-h-0">
          <div
            ref={labelsRef}
            className="shrink-0 overflow-y-auto overflow-x-hidden"
            style={{ scrollbarWidth: 'none' }}
            onScroll={onLabelsScroll}
          >
            <TrackLabels />
          </div>
          <div
            ref={canvasWrapRef}
            className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden"
            style={{ scrollbarWidth: 'none' }}
            onScroll={onCanvasScroll}
          >
            <TimelineCanvas />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/5 border border-dashed border-white/10">
            <Film size={16} className="text-white/30" />
            <span className="text-sm text-white/40">{t('editor.dragMediaHere', 'Drag and drop media here')}</span>
          </div>
        </div>
      )}
    </div>
  );
}
